import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SeoTags } from "../../../components/Head/SeoTags";
import { EncryptionMock } from "../../../components/Mockups/EncryptionMock";

const Encryption = () => {
  const { t } = useTranslation();

  return (
    <>
      <SeoTags
        title={t("featurePages.encryption.seo.title")}
        description={t("featurePages.encryption.seo.description")}
        keywords={t("featurePages.encryption.seo.keywords")}
      />

      {/* Structured Data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: t("featurePages.encryption.seo.title"),
            description: t("featurePages.encryption.seo.description"),
            url: "https://onbookmarks.com/features/encryption/",
            mainEntity: {
              "@type": "SoftwareApplication",
              name: "onBookmarks",
              applicationCategory: "ProductivityApplication",
              operatingSystem: "Web, iOS, Android",
              featureList: [
                "Client-side AES-GCM encryption",
                "Zero-knowledge architecture",
                "Salted PBKDF2 key derivation",
                "Selective bookmark encryption",
                "Your keys never leave your device"
              ],
              offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "USD",
                description: "Free plan includes encryption"
              }
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://onbookmarks.com"
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Features",
                  item: "https://onbookmarks.com/features/"
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: "Encryption",
                  item: "https://onbookmarks.com/features/encryption/"
                }
              ]
            }
          }),
        }}
      />

      <section className="hero bg-purple-subtle">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-6">
              <div className="d-flex flex-column gap-4">
                <div>
                  <span className="badge bg-purple text-white px-3 py-2">
                    {t("home.encryption.badge")}
                  </span>
                </div>
                <h1 className="display-4 fw-bold">
                  {t("featurePages.encryption.hero.title")}
                </h1>
                <p className="lead">
                  {t("featurePages.encryption.hero.description")}
                </p>
                <div>
                  <Link
                    to={`${process.env.REACT_APP_APP_URL}/register`}
                    className="btn btn-primary btn-lg px-4"
                  >
                    {t("featurePages.encryption.hero.cta")}
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <EncryptionMock />
            </div>
          </div>
        </div>
      </section>

      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center mb-5">
              <h2 className="h1 fw-bold mb-4">
                {t("featurePages.encryption.problem.title")}
              </h2>
              <p className="lead text-muted">
                {t("featurePages.encryption.problem.description")}
              </p>
            </div>
          </div>

          <div className="row g-4 mb-5">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-shield-x fs-2 text-danger mb-3"></i>
                  <h3 className="h5 mb-3">
                    {t("featurePages.encryption.problem.exposure.title")}
                  </h3>
                  <p className="text-muted mb-0">
                    {t("featurePages.encryption.problem.exposure.description")}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-eye fs-2 text-danger mb-3"></i>
                  <h3 className="h5 mb-3">
                    {t("featurePages.encryption.problem.serverAccess.title")}
                  </h3>
                  <p className="text-muted mb-0">
                    {t("featurePages.encryption.problem.serverAccess.description")}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-database-x fs-2 text-danger mb-3"></i>
                  <h3 className="h5 mb-3">
                    {t("featurePages.encryption.problem.breaches.title")}
                  </h3>
                  <p className="text-muted mb-0">
                    {t("featurePages.encryption.problem.breaches.description")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-light">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <div className="mb-3">
                <span className="badge bg-purple text-white px-3 py-2">
                  {t("featurePages.encryption.solution.badge")}
                </span>
              </div>
              <EncryptionMock />
            </div>
            <div className="col-lg-6">
              <h2 className="h1 fw-bold mb-4">
                {t("featurePages.encryption.solution.title")}
              </h2>
              <p className="lead mb-4">
                {t("featurePages.encryption.solution.description")}
              </p>
              <ul className="list-unstyled">
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">
                      {t("featurePages.encryption.solution.benefit1.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("featurePages.encryption.solution.benefit1.description")}
                    </p>
                  </div>
                </li>
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">
                      {t("featurePages.encryption.solution.benefit2.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("featurePages.encryption.solution.benefit2.description")}
                    </p>
                  </div>
                </li>
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">
                      {t("featurePages.encryption.solution.benefit3.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("featurePages.encryption.solution.benefit3.description")}
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="h1 fw-bold mb-4">
              {t("featurePages.encryption.howItWorks.title")}
            </h2>
            <p className="lead text-muted">
              {t("featurePages.encryption.howItWorks.description")}
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="text-center">
                <div className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle">
                  <span className="fs-2 fw-bold text-purple">1</span>
                </div>
                <h3 className="h5 mb-3">
                  {t("featurePages.encryption.howItWorks.step1.title")}
                </h3>
                <p className="text-muted">
                  {t("featurePages.encryption.howItWorks.step1.description")}
                </p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-center">
                <div className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle">
                  <span className="fs-2 fw-bold text-purple">2</span>
                </div>
                <h3 className="h5 mb-3">
                  {t("featurePages.encryption.howItWorks.step2.title")}
                </h3>
                <p className="text-muted">
                  {t("featurePages.encryption.howItWorks.step2.description")}
                </p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-center">
                <div className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle">
                  <span className="fs-2 fw-bold text-purple">3</span>
                </div>
                <h3 className="h5 mb-3">
                  {t("featurePages.encryption.howItWorks.step3.title")}
                </h3>
                <p className="text-muted">
                  {t("featurePages.encryption.howItWorks.step3.description")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <h2 className="h1 fw-bold mb-4">
                {t("featurePages.encryption.useCases.title")}
              </h2>
              <p className="lead text-muted mb-5">
                {t("featurePages.encryption.useCases.description")}
              </p>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-briefcase fs-3 text-purple me-3"></i>
                    <h3 className="h5 mb-0">
                      {t("featurePages.encryption.useCases.business.title")}
                    </h3>
                  </div>
                  <p className="text-muted mb-3">
                    {t("featurePages.encryption.useCases.business.description")}
                  </p>
                  <ul className="list-unstyled mb-0">
                    <li className="mb-2">
                      <i className="bi bi-arrow-right text-purple me-2"></i>
                      {t("featurePages.encryption.useCases.business.example1")}
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-arrow-right text-purple me-2"></i>
                      {t("featurePages.encryption.useCases.business.example2")}
                    </li>
                    <li>
                      <i className="bi bi-arrow-right text-purple me-2"></i>
                      {t("featurePages.encryption.useCases.business.example3")}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-person-lock fs-3 text-purple me-3"></i>
                    <h3 className="h5 mb-0">
                      {t("featurePages.encryption.useCases.personal.title")}
                    </h3>
                  </div>
                  <p className="text-muted mb-3">
                    {t("featurePages.encryption.useCases.personal.description")}
                  </p>
                  <ul className="list-unstyled mb-0">
                    <li className="mb-2">
                      <i className="bi bi-arrow-right text-purple me-2"></i>
                      {t("featurePages.encryption.useCases.personal.example1")}
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-arrow-right text-purple me-2"></i>
                      {t("featurePages.encryption.useCases.personal.example2")}
                    </li>
                    <li>
                      <i className="bi bi-arrow-right text-purple me-2"></i>
                      {t("featurePages.encryption.useCases.personal.example3")}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pocket Migration Callout */}
      <section className="py-4">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="card border-0 bg-light">
                <div className="card-body p-4 d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
                  <div className="d-flex align-items-center gap-3">
                    <i className="bi bi-arrow-repeat fs-3 text-danger"></i>
                    <div>
                      <h4 className="h6 mb-1">Coming from Pocket?</h4>
                      <p className="text-muted mb-0 small">Unlike Pocket, onBookmarks offers true zero-knowledge encryption. Your private bookmarks stay private.</p>
                    </div>
                  </div>
                  <Link to="/pocket-alternative" className="btn btn-outline-primary btn-sm text-nowrap">
                    See Pocket Comparison
                    <i className="bi bi-arrow-right ms-2"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5">
        <div className="container">
          <div className="bg-purple rounded-4 p-5 text-center text-white">
            <h2 className="h1 fw-bold mb-4">
              {t("featurePages.encryption.cta.title")}
            </h2>
            <p className="lead mb-4">
              {t("featurePages.encryption.cta.description")}
            </p>
            <Link
              to={`${process.env.REACT_APP_APP_URL}/register`}
              className="btn btn-light btn-lg px-5"
            >
              {t("featurePages.encryption.cta.button")}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Encryption;